import {
  DEFAULT_COMMITTEE_REVIEWERS,
  loadReviewWorkflows,
  saveReviewWorkflows
} from '@/utils/admin-review-workflow'
import { isReadyForAdminFinalApproval } from '@/utils/review-final-approval'

const AGREEMENTS_KEY = 'user-agreement-workflows-v1'
const CLAUSES_KEY = 'user-clause-library-v1'

const DEFAULT_CLAUSES = [
  { id: 'cl-1', title: 'Purpose of Cooperation', content: 'The parties agree to promote academic cooperation in areas of mutual interest.', category: 'General', appliesTo: ['Exchange Student', 'Double Degree', 'Extension Agreement', 'MOU File'], sourceType: 'default', editable: false },
  { id: 'cl-2', title: 'Student Mobility', content: 'The parties may exchange students for study, research, and academic activities subject to institutional approval.', category: 'Academic', appliesTo: ['Exchange Student', 'Double Degree'], sourceType: 'default', editable: false },
  { id: 'cl-3', title: 'Financial Responsibilities', content: 'Each party remains responsible for its own costs unless otherwise agreed in writing.', category: 'Financial', appliesTo: ['Exchange Student', 'Double Degree', 'Extension Agreement', 'MOU File'], sourceType: 'default', editable: false },
  { id: 'cl-4', title: 'Confidentiality', content: 'Both parties must keep confidential information obtained through this agreement secure and protected.', category: 'Legal', appliesTo: ['Double Degree', 'Extension Agreement', 'MOU File'], sourceType: 'default', editable: false },
  { id: 'cl-5', title: 'Termination', content: 'Either party may terminate the agreement by giving prior written notice within the agreed notice period.', category: 'Legal', appliesTo: ['Exchange Student', 'Double Degree', 'Extension Agreement', 'MOU File'], sourceType: 'default', editable: false }
]

const TYPE_ROUTE_MAP = {
  'Exchange Student': 'exchange',
  'Double Degree': 'double-degree',
  'Extension Agreement': 'extension',
  'MOU File': 'mou'
}

function clone(value) {
  return JSON.parse(JSON.stringify(value))
}

function readStorage(key, fallback) {
  if (typeof window === 'undefined' || !window.localStorage) return clone(fallback)
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) {
      window.localStorage.setItem(key, JSON.stringify(fallback))
      return clone(fallback)
    }
    return JSON.parse(raw)
  } catch (error) {
    window.localStorage.setItem(key, JSON.stringify(fallback))
    return clone(fallback)
  }
}

function writeStorage(key, value) {
  if (typeof window === 'undefined' || !window.localStorage) return
  window.localStorage.setItem(key, JSON.stringify(value))
}

function nowIso() {
  return new Date().toISOString()
}

function getCurrentUserName() {
  if (typeof window === 'undefined' || !window.localStorage) return 'Current User'
  try {
    const raw = window.localStorage.getItem('objs')
    if (!raw) return 'Current User'
    const data = JSON.parse(raw)
    const profile = data && (data.user || data.accounts || data.profile || data)
    const firstName = profile && profile.firstName ? String(profile.firstName) : ''
    const lastName = profile && profile.lastName ? String(profile.lastName) : ''
    const name = [firstName, lastName].filter(Boolean).join(' ').trim()
    return name || String(profile && (profile.name || profile.email || 'Current User'))
  } catch (error) {
    return 'Current User'
  }
}

function emptyAgreementDraft() {
  return {
    id: 0,
    title: '',
    partner: '',
    country: '',
    partnerEmail: '',
    contactPerson: '',
    department: 'International Affairs',
    activityType: 'Exchange Student',
    draftSource: 'template',
    templateFields: {},
    customDocument: {
      title: '',
      content: ''
    },
    uploadedDraft: null,
    selectedClauseIds: [],
    selectedClauses: [],
    summary: '',
    responseNote: '',
    statusKey: 'draft',
    createdAt: '',
    updatedAt: '',
    submittedAt: '',
    timeline: []
  }
}

function normalizeAgreement(item) {
  const base = emptyAgreementDraft()
  return {
    ...base,
    ...item,
    templateFields: Object.assign({}, base.templateFields, item && item.templateFields),
    customDocument: Object.assign({}, base.customDocument, item && item.customDocument),
    selectedClauseIds: Array.isArray(item && item.selectedClauseIds) ? item.selectedClauseIds : [],
    selectedClauses: Array.isArray(item && item.selectedClauses) ? item.selectedClauses : [],
    timeline: Array.isArray(item && item.timeline) ? item.timeline : []
  }
}

export function loadUserClauses() {
  return readStorage(CLAUSES_KEY, DEFAULT_CLAUSES).map(item => ({
    ...item,
    editable: item.sourceType !== 'default'
  }))
}

function saveUserClauses(items) {
  writeStorage(CLAUSES_KEY, items)
}

export function createUserClause(payload) {
  const items = loadUserClauses()
  const nextItem = {
    id: 'custom-' + Date.now(),
    title: payload.title,
    content: payload.content,
    category: payload.category || 'General',
    appliesTo: Array.isArray(payload.appliesTo) && payload.appliesTo.length ? payload.appliesTo : ['MOU File'],
    sourceType: 'custom',
    editable: true
  }
  items.unshift(nextItem)
  saveUserClauses(items)
  return nextItem
}

export function updateUserClause(id, payload) {
  const items = loadUserClauses()
  const index = items.findIndex(item => String(item.id) === String(id))
  if (index === -1) return null
  items.splice(index, 1, {
    ...items[index],
    ...payload,
    editable: true,
    sourceType: 'custom'
  })
  saveUserClauses(items)
  return items[index]
}

export function deleteUserClause(id) {
  const items = loadUserClauses().filter(item => String(item.id) !== String(id))
  saveUserClauses(items)
}

export function loadUserAgreements() {
  return readStorage(AGREEMENTS_KEY, []).map(normalizeAgreement)
}

function saveUserAgreements(items) {
  writeStorage(AGREEMENTS_KEY, items.map(normalizeAgreement))
}

function appendTimeline(item, action, meta) {
  return {
    ...item,
    timeline: [
      {
        action,
        date: nowIso(),
        user: getCurrentUserName(),
        meta: meta || ''
      },
      ...(item.timeline || [])
    ]
  }
}

function workflowStageFromAgreement(item) {
  if (item.statusKey === 'approved') return { status: 'approved', currentStep: 4 }
  if (item.statusKey === 'final-review') return { status: 'pending', currentStep: 4 }
  if (item.statusKey === 'suggestion') return { status: 'pending', currentStep: 3 }
  if (item.statusKey === 'submitted') return { status: 'pending', currentStep: 3 }
  return { status: 'draft', currentStep: 1 }
}

function syncAgreementToAdminWorkflow(agreement) {
  const items = loadReviewWorkflows()
  const index = items.findIndex(item => Number(item.id) === Number(agreement.id))
  if (agreement.statusKey === 'draft' && index === -1) return
  const stage = workflowStageFromAgreement(agreement)
  const existing = index >= 0 ? items[index] : null

  const userComments = Array.isArray(existing && existing.comments) ? existing.comments.filter(comment => comment.role !== 'User') : []
  const nextComments = agreement.responseNote
    ? [
        ...userComments,
        {
          id: 'user-' + agreement.id + '-' + Date.now(),
          author: getCurrentUserName(),
          role: 'User',
          time: new Date().toLocaleString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }),
          page: 1,
          section: agreement.statusKey === 'final-review' ? 'Final revised draft' : 'Draft submission',
          copy: agreement.responseNote
        }
      ]
    : userComments

  const availableReviewers = existing && Array.isArray(existing.availableReviewers) && existing.availableReviewers.length
    ? existing.availableReviewers
    : DEFAULT_COMMITTEE_REVIEWERS
  const assignedReviewerIds = stage.currentStep >= 3
    ? availableReviewers.map(item => item.id)
    : existing && existing.assignedReviewerIds
      ? existing.assignedReviewerIds
      : []
  const committees = stage.currentStep >= 3
    ? availableReviewers.map(item => item.name)
    : existing && existing.committees
      ? existing.committees
      : []

  const nextItem = {
    id: agreement.id,
    title: agreement.title || 'Untitled agreement',
    type: agreement.activityType,
    partner: agreement.partner || 'Partner institution',
    department: agreement.department || 'International Affairs',
    createdBy: getCurrentUserName(),
    createdAt: existing && existing.createdAt
      ? existing.createdAt
      : (agreement.createdAt || nowIso()),
    updatedAt: agreement.updatedAt || (existing && existing.updatedAt) || agreement.submittedAt || agreement.createdAt || nowIso(),
    submittedAt: agreement.submittedAt || (existing && existing.submittedAt) || agreement.createdAt || nowIso(),
    status: stage.status,
    summary: agreement.summary || 'User-created agreement draft awaiting review workflow updates.',
    documentCode: 'MOU-' + agreement.id,
    currentStep: stage.currentStep,
    approvedAt: stage.status === 'approved' ? nowIso().slice(0, 10) : '',
    decisionNote: existing && existing.decisionNote ? existing.decisionNote : '',
    fileLabel: agreement.uploadedDraft && agreement.uploadedDraft.name ? agreement.uploadedDraft.name : (agreement.title || 'Agreement Draft') + '.docx',
    attachmentOwnerType: 'mou-review',
    attachmentOwnerId: 'mou-' + agreement.id,
    committees,
    availableReviewers,
    assignedReviewerIds,
    comments: nextComments
  }

  if (index >= 0) items.splice(index, 1, { ...existing, ...nextItem })
  else items.unshift(nextItem)
  saveReviewWorkflows(items)
}

function deriveAgreementStage(agreement, workflow) {
  if (workflow && workflow.status === 'approved') return 'approved'
  if (workflow && workflow.status === 'rejected') return 'revision'
  if (workflow && workflow.status === 'pending' && isReadyForAdminFinalApproval(workflow)) return 'final-review'
  if (workflow && workflow.currentStep >= 3 && workflow.status === 'pending') return 'suggestion'
  if (workflow && workflow.currentStep >= 2) return 'submitted'
  return agreement.statusKey || 'draft'
}

function stageLabel(stage) {
  return {
    draft: 'Draft',
    submitted: 'Sent to Committee Review',
    suggestion: 'Under Committee Review',
    'final-review': 'Waiting for Admin Final Decision',
    approved: 'Approved',
    revision: 'Returned for Revision'
  }[stage] || 'Draft'
}

export function getAgreementTypeRoute(type) {
  return TYPE_ROUTE_MAP[type] || 'exchange'
}

export function listUserAgreementRecords() {
  const agreements = loadUserAgreements()
  const workflows = loadReviewWorkflows()

  return agreements.map((agreement) => {
    const workflow = workflows.find(item => Number(item.id) === Number(agreement.id)) || null
    const stage = deriveAgreementStage(agreement, workflow)
    return {
      ...agreement,
      workflow,
      derivedStage: stage,
      derivedStageLabel: stageLabel(stage),
      status: stageLabel(stage),
      routeType: getAgreementTypeRoute(agreement.activityType)
    }
  }).sort((left, right) => new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime())
}

export function getUserAgreementRecord(id) {
  return listUserAgreementRecords().find(item => String(item.id) === String(id)) || null
}

export function createAgreementDraft(payload) {
  const items = loadUserAgreements()
  const id = Date.now()
  const nextItem = appendTimeline(normalizeAgreement({
    ...emptyAgreementDraft(),
    id,
    title: payload.title,
    partner: payload.partner,
    partnerEmail: payload.partnerEmail,
    contactPerson: payload.contactPerson,
    country: payload.country,
    activityType: payload.activityType || 'Exchange Student',
    summary: payload.summary || '',
    createdAt: nowIso(),
    updatedAt: nowIso()
  }), 'Draft created')
  items.unshift(nextItem)
  saveUserAgreements(items)
  syncAgreementToAdminWorkflow(nextItem)
  return getUserAgreementRecord(id)
}

export function updateAgreementDraft(id, payload) {
  const items = loadUserAgreements()
  const index = items.findIndex(item => String(item.id) === String(id))
  if (index === -1) return null
  const nextItem = normalizeAgreement({
    ...items[index],
    ...payload,
    updatedAt: nowIso()
  })
  items.splice(index, 1, nextItem)
  saveUserAgreements(items)
  syncAgreementToAdminWorkflow(nextItem)
  return getUserAgreementRecord(id)
}

export function saveAgreementClauses(id, selectedClauseIds) {
  const clauses = loadUserClauses()
  const selected = clauses.filter(item => selectedClauseIds.includes(String(item.id)) || selectedClauseIds.includes(item.id))
  return updateAgreementDraft(id, {
    selectedClauseIds: selectedClauseIds.map(String),
    selectedClauses: selected
  })
}

export function submitAgreement(id, payload) {
  const items = loadUserAgreements()
  const index = items.findIndex(item => String(item.id) === String(id))
  if (index === -1) return null
  let nextItem = normalizeAgreement({
    ...items[index],
    ...payload,
    statusKey: 'submitted',
    submittedAt: nowIso(),
    updatedAt: nowIso()
  })
  nextItem = appendTimeline(nextItem, 'Agreement submitted', 'Waiting for admin review')
  items.splice(index, 1, nextItem)
  saveUserAgreements(items)
  syncAgreementToAdminWorkflow(nextItem)
  return getUserAgreementRecord(id)
}

export function resubmitAgreementRevision(id, payload) {
  const items = loadUserAgreements()
  const index = items.findIndex(item => String(item.id) === String(id))
  if (index === -1) return null
  let nextItem = normalizeAgreement({
    ...items[index],
    ...payload,
    statusKey: 'final-review',
    updatedAt: nowIso()
  })
  nextItem = appendTimeline(nextItem, 'Revision resubmitted', 'Final draft sent back to admin')
  items.splice(index, 1, nextItem)
  saveUserAgreements(items)
  syncAgreementToAdminWorkflow(nextItem)
  return getUserAgreementRecord(id)
}

export function deleteAgreementDraftRecord(id) {
  const items = loadUserAgreements()
  const nextItems = items.filter(item => String(item.id) !== String(id))
  saveUserAgreements(nextItems)

  const reviewItems = loadReviewWorkflows()
  const nextReviewItems = reviewItems.filter(item => String(item.id) !== String(id))
  saveReviewWorkflows(nextReviewItems)

  return true
}

export function listUserReviewQueueRecords() {
  const agreements = listUserAgreementRecords()
  return {
    pending: agreements.filter(item => item.derivedStage !== 'approved' && item.derivedStage !== 'draft'),
    history: agreements.filter(item => item.derivedStage === 'approved')
  }
}

export function buildAgreementApiResponse(method, data) {
  if (method === 'list') return listUserAgreementRecords()
  if (method === 'detail') return getUserAgreementRecord(data && data.id)
  if (method === 'create-draft') return createAgreementDraft(data || {})
  if (method === 'update-draft') return updateAgreementDraft(data && data.id, data || {})
  if (method === 'submit') return submitAgreement(data && data.id, data || {})
  if (method === 'resubmit') return resubmitAgreementRevision(data && data.id, data || {})
  if (method === 'clauses') {
    const agreement = getUserAgreementRecord(data && data.id)
    const clauses = loadUserClauses()
    const categories = [...new Set(clauses.map(item => item.category))]
    const type = agreement && agreement.activityType ? agreement.activityType : 'Exchange Student'
    const suggestedClauseIds = clauses.filter(item => !item.appliesTo || item.appliesTo.includes(type)).slice(0, 3).map(item => item.id)
    return {
      clauses,
      categories,
      selectedClauses: agreement && agreement.selectedClauses ? agreement.selectedClauses : [],
      suggestedClauseIds
    }
  }
  if (method === 'update-clauses') return saveAgreementClauses(data && data.id, data && data.selectedClauses ? data.selectedClauses : [])
  if (method === 'review-queue') return listUserReviewQueueRecords()
  if (method === 'user-clauses') return loadUserClauses()
  if (method === 'create-clause') return createUserClause(data || {})
  if (method === 'update-clause') return updateUserClause(data && data.id, data || {})
  if (method === 'delete-clause') return deleteUserClause(data && data.id)
  return null
}
